/* The top-level component for the TempIncome page */

/* eslint-disable no-restricted-syntax */
/* eslint-disable guard-for-in */
/* eslint-disable no-use-before-define */

import React, { useEffect, useState, useMemo } from "react";
import * as moment from "moment";
import { connect } from "react-redux";
import {
  Button,
  Checkbox,
  DatePicker,
  DatePickerInput,
  InlineNotification,
  Loading,
  Modal,
  RadioButton,
  RadioButtonGroup,
  Table as CarbonTable,
  TableBody,
  TableCell,
  TableRow,
  Toggle,
  Tooltip,
} from "carbon-components-react";
import { Checkmark32, Save16 } from "@carbon/icons-react";
import BaseTable, { EditingState } from "web_modules/shared/BaseTable";
import {
  EmploymentShape,
  LoanApplicationBorrower,
  MilitaryServiceShape,
  UpdatePropertyTypeRequest,
} from "@eave/shared/types/api";
import { formatCurrency } from "@eave/shared/utils/currency";
import { addressToPostalFormatStr } from "@eave/shared/utils/addresses";
import HurdleView from "underwriter_dashboard/shared/HurdleView";
import {
  LoadingState,
  IncomeType,
  EmploymentType,
  incomeTypeToStr,
} from "underwriter_dashboard/shared/types";
import { HurdleProps } from "underwriter_dashboard/HurdleEditor";
import APIClient from "web_modules/APIClient";
import Toast, { useToast } from "web_modules/shared/Toast";
import { logException } from "web_modules/utils/sentry";
import LearnMore from "web_modules/shared/LearnMore";
import { TableHeaderDataShape } from "web_modules/shared/BaseTable/types";

import {
  documentsForOwner,
  getOtherIncomesForBorrower,
  propertyLabelAndAddressOptions,
  propertyLabelAndAddressOptionSelected,
  rentalProperties,
  taxReturns,
} from "./data-helpers";
import { addIncomeMapToEmployments } from "./helpers";
import { initialValues } from "./form-values";
import EmploymentModalForm from "./forms/EmploymentModalForm";
import EmploymentSection from "./income_tables/expanded_row_partials/EmploymentSection";
import { mapDispatchToProps } from "../selectors";
import OtherIncomeTable from "./income_tables/OtherIncomeTable";
import {
  calculateMonthlyIncomeForEmployments,
  calculateMonthlyIncomeForOtherIncomes,
  TaxReturnIncomeTypeToDisplayText,
  ModifiedAggregationMap,
} from "./shared";
import RentalIncome from "../IncomeDocumentsReview/RentalIncome";
import TaxSummarySubsection from "./income_tables/expanded_row_partials/TaxSummarySubsection";
import { updateIncomeAggregationRules } from "./api-helpers";
import SubjectPropertyRentalIncomeTable from "underwriter_dashboard/hurdle_editors/Income/income_tables/SubjectPropertyRentalIncomeTable";
import Big from "big.js";
import BorrowerEntityUtil from "@eave/shared/utils/borrowers";

const styles = require("underwriter_dashboard/hurdle_editors/styles.module.scss");
const incomeStyles = require("./styles.module.scss");

interface HurdleEditorProps extends HurdleProps {
  loadApp: Function;
}

interface MilitaryServiceHurdleShape {
  ever_serve: boolean;
  active_duty_expiration: string;
  active_duty: boolean;
  retired_discharged_separated: boolean;
  non_activated_reserve: boolean;
  surviving_spouse: boolean;
}

const NewIncomeEditor: React.FC<HurdleEditorProps> = ({ app, hurdle, loadApp, onHide }) => {
  const {
    all_documents,
    borrowers,
    employments: appEmployments,
    has_borrower2,
    income_summary,
    property_is_mixed_use,
  } = app;
  const hasCoborrower = has_borrower2;

  // updates
  // An object to track those income aggregations that have been updated
  const [modifiedAggregations, setModifiedAggregations] = useState<ModifiedAggregationMap>({});

  const { toastHooks, notifyFunctions } = useToast();
  const { notifySuccess, notifyError } = notifyFunctions;

  const [rowsExpanded, setRowsExpanded] = useState(null);
  const [employments, setEmployments] = useState(addIncomeMapToEmployments(appEmployments));
  // Initial fetching
  useEffect(() => {
    Object.keys(borrowers).forEach(borrowerEntity => {
      loadMilitaryServiceFromAPI(app.id, Number(borrowerEntity));
    });
  }, [app.id]);

  const [employmentFormState, updateEmploymentFormState] = useState(initialValues);

  // military service methods
  const processMilitaryServiceSuccessAPIResponse = (
    militaryService: MilitaryServiceShape,
    borrowerEntity: number,
  ) => {
    setMilitaryServiceStateForBorrowerFromAPI(borrowerEntity, militaryService);
    updateLoadingState(LoadingState.FINISHED);
  };

  const processMilitaryServiceFailureAPIResponse = (err: any, borrowerEntity: number) => {
    logException(err);
    if (err && err.meta && err.meta.error_detail) {
      updateMilitaryServiceErrorMessageForBorrower(borrowerEntity, err.meta.error_detail);
    }
    updateLoadingState(LoadingState.FINISHED);
  };

  const isValidMilitaryServiceState = (stateForBorrower: MilitaryServiceHurdleShape) => {
    const hasServiceType =
      stateForBorrower.active_duty ||
      stateForBorrower.non_activated_reserve ||
      stateForBorrower.retired_discharged_separated ||
      stateForBorrower.surviving_spouse;

    if (!!stateForBorrower.ever_serve !== !!hasServiceType) {
      return "If indicating service, please specify type.";
    }

    if (stateForBorrower.active_duty && !stateForBorrower.active_duty_expiration) {
      return "Please specify active duty expiration date";
    }

    // the form state update handler manages this, so shouldn't be reached ever,
    // but still an invalid state
    if (!stateForBorrower.active_duty && stateForBorrower.active_duty_expiration) {
      return "If not on active duty, then should be no active duty expiration date";
    }

    // all good
    return "";
  };

  const persistMilitaryServiceForBorrowerAtAPI = (
    appID: number,
    borrowerEntity: number,
    militaryServiceStateForBorrower: MilitaryServiceHurdleShape,
  ) => {
    updateLoadingState(LoadingState.ACTIVE);

    return APIClient.post(
      `/uw/${appID}/military_service/${borrowerEntity}`,
      militaryServiceStateForBorrower,
    )
      .then((response: any) => {
        processMilitaryServiceSuccessAPIResponse(response.military_service, borrowerEntity);
        return true;
      })
      .catch(err => {
        processMilitaryServiceFailureAPIResponse(err, borrowerEntity);
        return false;
      });
  };

  // returns false if errors for either borrower, since in that case,
  // need to prevent reload -- so that error is displayed to operator.
  const persistMilitaryServiceAtAPI = async () => {
    const promises = Object.keys(borrowers).map(borrowerEntity => {
      if (!militaryServiceStateChanged[Number(borrowerEntity)]) {
        // no error, since nothing to persist
        return Promise.resolve(true);
      }

      const militaryServiceStateForBorrower: MilitaryServiceHurdleShape =
        militaryServiceState[Number(borrowerEntity)];
      const invalidMessage = isValidMilitaryServiceState(militaryServiceStateForBorrower);
      if (invalidMessage !== "") {
        updateMilitaryServiceErrorMessageForBorrower(Number(borrowerEntity), invalidMessage);
        return Promise.resolve(false);
      }
      return persistMilitaryServiceForBorrowerAtAPI(
        app.id,
        Number(borrowerEntity),
        militaryServiceStateForBorrower,
      );
    });
    const values = await Promise.all(promises);
    return values.every(v => v);
  };

  const loadMilitaryServiceFromAPI = (appID: number, borrowerEntity: number) => {
    updateLoadingState(LoadingState.ACTIVE);

    APIClient.get(`/uw/${appID}/military_service/${borrowerEntity}`)
      .then((response: any) => {
        processMilitaryServiceSuccessAPIResponse(response.military_service, borrowerEntity);
      })
      .catch(err => {
        if (err && err.meta && err.meta.code === 404) {
          processMilitaryServiceSuccessAPIResponse(null, borrowerEntity);
        } else {
          processMilitaryServiceFailureAPIResponse(err, borrowerEntity);
        }
      });
  };

  const setMilitaryServiceStateForBorrowerFromAPI = (
    borrowerEntity: number,
    militaryService: MilitaryServiceShape,
  ) => {
    const existsOrDefault = (key: string, defaultValue: any) =>
      militaryService ? militaryService[key] : defaultValue;

    const militaryServiceForBorrower = {
      ever_serve: existsOrDefault("ever_serve", false),
      active_duty: militaryService ? militaryService.active_duty_expiration !== null : false, // set the one computed field
      active_duty_expiration: existsOrDefault("active_duty_expiration", null),
      retired_discharged_separated: existsOrDefault("retired_discharged_separated", false),
      non_activated_reserve: existsOrDefault("non_activated_reserve", false),
      surviving_spouse: existsOrDefault("surviving_spouse", false),
    };
    setInitialMilitaryServiceStateForBorrower(borrowerEntity, militaryServiceForBorrower);
  };

  const initialMilitaryServiceState = Object.keys(borrowers).reduce(
    (acc, curr) => ({
      ...acc,
      [curr]: {},
    }),
    {},
  );
  const [militaryServiceState, updateMilitaryServiceState] = useState(initialMilitaryServiceState);

  const initialMilitaryServiceStateChanged = Object.keys(borrowers).reduce(
    (acc, curr) => ({
      ...acc,
      [curr]: false,
    }),
    {},
  );
  // naive "do we need to update" / "was touched" boolean trackers.
  // just using to avoid those API update calls that are easily avoidable.
  const [militaryServiceStateChanged, updateMilitaryServiceStateChanged] = useState(
    initialMilitaryServiceStateChanged,
  );

  const setInitialMilitaryServiceStateForBorrower = (
    borrowerEntity: number,
    fullState: MilitaryServiceHurdleShape,
  ) => {
    updateMilitaryServiceState(prevState => ({
      ...prevState,
      [borrowerEntity]: fullState,
    }));
    updateMilitaryServiceStateChanged(prevState => ({
      ...prevState,
      [borrowerEntity]: false,
    }));
    updateMilitaryServiceErrorMessageForBorrower(borrowerEntity, "");
  };

  const updateMilitaryServiceStateForBorrower = (borrowerEntity: number, change: object) => {
    updateMilitaryServiceState(prevState => ({
      ...prevState,
      [borrowerEntity]: {
        ...prevState[borrowerEntity],
        ...change,
      },
    }));
    updateMilitaryServiceStateChanged(prevState => ({
      ...prevState,
      [borrowerEntity]: true,
    }));
    updateMilitaryServiceErrorMessageForBorrower(borrowerEntity, "");
  };

  const initialMilitaryServiceErrorMessage = Object.keys(borrowers).reduce(
    (acc, curr) => ({
      ...acc,
      [curr]: "",
    }),
    {},
  );
  const [militaryServiceErrorMessage, updateMilitaryServiceErrorMessage] = useState(
    initialMilitaryServiceErrorMessage,
  );

  const updateMilitaryServiceErrorMessageForBorrower = (
    borrowerEntity: number,
    message: string,
  ) => {
    updateMilitaryServiceErrorMessage(prevState => ({
      ...prevState,
      [borrowerEntity]: message,
    }));
  };
  // end military service methods

  // Modals
  const [employmentModalIsOpen, updateEmploymentModalIsOpen] = useState(false);
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);

  // Batch delete hooks
  const [batchDeleteModalIsOpen, setBatchDeleteModalIsOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  const [editingMode, updateEditingMode] = useState<EditingState>(EditingState.CREATE);
  const [loadingState, updateLoadingState] = useState(LoadingState.INACTIVE);

  const preparePayload = (values): any => ({
    ...values,
    self_employed_ownership_percent: values.self_employed_ownership_percent || null,
  });

  const createEmployment = values => {
    updateLoadingState(LoadingState.ACTIVE);

    const payload = preparePayload(values);

    // We're creating a new employment, so don't send ID
    delete payload.id;

    APIClient.post(`/uw/${app.id}/employments`, payload)
      .then(() => {
        updateLoadingState(LoadingState.FINISHED);
        updateEmploymentModalIsOpen(false);
        loadApp(app.id);
      })
      .catch(err => {
        updateLoadingState(LoadingState.FINISHED);
        // TODO: Add toast notifications (or something) indicating failure
      });
  };

  const deleteEmployment = employmentID => {
    APIClient.deleteRequest(`/uw/${app.id}/employments/${employmentID}`).then(() => {
      updateEmploymentFormState(initialValues);
      loadApp(app.id);
    });
  };

  const updateEmployment = values => {
    updateLoadingState(LoadingState.ACTIVE);

    const payload = preparePayload(values);

    APIClient.put(`/uw/${app.id}/employments`, payload)
      .then(() => {
        updateEmploymentFormState(initialValues);
        updateLoadingState(LoadingState.FINISHED);
        updateEmploymentModalIsOpen(false);
        loadApp(app.id);
      })
      .catch(err => {
        updateLoadingState(LoadingState.FINISHED);
      });
  };

  const updateMainIncome = (id: number, income_source: string, borrowerEntity: number) => {
    const updateLoanApp = APIClient.put(`/loan_applications/${app.id}`, {
      [`borrower${borrowerEntity}_primary_income_id`]: id,
      [`borrower${borrowerEntity}_primary_income_source`]: income_source,
    });
    const borrower = borrowers[borrowerEntity];
    const updateLoanAppBorrower = APIClient.put(
      `/uw/loan_applications/${app.id}/loan_application_borrowers/entity/${borrowerEntity}`,
      {
        borrower: {
          ...borrower,
          loan_application_id: app.id,
          primary_income_id: id,
          primary_income_source: income_source,
        },
      },
    );

    Promise.all([updateLoanApp, updateLoanAppBorrower]).then(() => {
      loadApp(app.id);
    });
  };

  const toggleIncludeInRatios = (includeInRatio: boolean, employmentIdx: number) => {
    const newAggregations = employments[employmentIdx].income_aggregations?.map(incomeAgg => ({
      ...incomeAgg,
      include_in_ratios: includeInRatio,
    }));
    if (!newAggregations || !newAggregations.length) return;

    APIClient.put(`/${app.id}/paystub_income_aggregations`, {
      income_aggregations: newAggregations,
    }).then(() =>
      setEmployments(emp =>
        emp.map((emp, idx) => {
          return idx === employmentIdx
            ? { ...emp, income_aggregations: newAggregations }
            : { ...emp };
        }),
      ),
    );
  };

  const onSave = async () => {
    if (!(await persistMilitaryServiceAtAPI())) {
      // if there was an error, return so that it is displayed to operator,
      // rather than page reloaded, which would clear error.
      return;
    }

    const aggregations = [];
    employments.forEach(employment => {
      for (const j in employment.income_aggregations) {
        aggregations.push(employment.income_aggregations[j]);
      }
    });

    const promises = [];
    promises.push(
      APIClient.put(`/${app.id}/paystub_income_aggregations`, {
        income_aggregations: aggregations,
      }),
    );
    promises.push(updateIncomeAggregationRules(app.id, Object.values(modifiedAggregations)));

    Promise.all(promises)
      .then(() => {
        notifySuccess("Income Aggregation Rules Updated");
        setModifiedAggregations({});
      })
      .catch(err => {
        notifyError(`Error updating income aggregation rules: ${err}`);
      });
  };

  const updateMixedUse = (v: string) => {
    updateLoadingState(LoadingState.ACTIVE);

    const is_mixed_use: boolean = v === "yes";
    const url = `/loan_applications/${app.id}/hurdles/operator_subject_property_type`;

    const data: UpdatePropertyTypeRequest = {
      is_mixed_use,
    };

    return APIClient.post(url, data).then(() => updateLoadingState(LoadingState.INACTIVE));
  };

  const renderMixedProperty = () => {
    const initialValue = property_is_mixed_use ? "yes" : "no";

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          height: "50px",
          paddingLeft: "15px",
          marginRight: "1rem",
        }}
      >
        <p className={incomeStyles.question}>
          Will the borrower set aside space in the subject property to operate a business that
          customers visit:
          <LearnMore text="This should only be completed for owner-occupied properties. <br /><br /> Only select Yes if the property will have commercial space set aside such as a daycare, medical office, beauty/barber shop. <br /> <br />This should not be marked Yes for home offices used by remote employees." />
        </p>
        <RadioButtonGroup
          id="property-is-mixed-use"
          name="property_is_mixed_use"
          valueSelected={initialValue}
          onChange={value => updateMixedUse(value)}
        >
          <RadioButton value="yes" labelText="Yes" id="is_mixed" />
          <RadioButton value="no" labelText="No" id="is_not_mixed" />
        </RadioButtonGroup>
      </div>
    );
  };

  const renderMilitaryService = (borrowerEntity: number, borrowerName: string) => {
    const militaryServiceStateForBorrower = militaryServiceState
      ? militaryServiceState[borrowerEntity]
      : {};
    const militaryServiceErrorMessageForBorrower = militaryServiceErrorMessage
      ? militaryServiceErrorMessage[borrowerEntity]
      : "";

    return (
      <>
        <div
          style={{
            paddingLeft: "15px",
            marginRight: "1rem",
          }}
        >
          {militaryServiceErrorMessageForBorrower.length > 0 && (
            <InlineNotification
              kind="error"
              notificationType="inline"
              role="alert"
              statusIconDescription="Error"
              subtitle={militaryServiceErrorMessageForBorrower}
              title="Error"
              onCloseButtonClick={() => {
                updateMilitaryServiceErrorMessageForBorrower(borrowerEntity, "");
              }}
            />
          )}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              height: "50px",
            }}
          >
            <p className={incomeStyles.question}>
              {borrowerName}: Military Service
              <LearnMore
                text={`This answers the question on the loan application: "Did the borrower (or deceased spouse) ever serve, or are they currently serving, in the United States Armed Forces?"`}
              />
            </p>
            <RadioButtonGroup
              id={`${borrowerEntity}-military-service`}
              name={`${borrowerEntity}-military-service`}
              valueSelected={militaryServiceStateForBorrower.ever_serve === true ? "yes" : "no"}
              onChange={value => {
                let change: any = {
                  ever_serve: value === "yes" ? true : false,
                };
                if (!change.ever_serve) {
                  // if setting as never served, clear sub-values
                  change = {
                    ...change,
                    active_duty: false,
                    active_duty_expiration: null,
                    retired_discharged_separated: false,
                    non_activated_reserve: false,
                    surviving_spouse: false,
                  };
                }
                updateMilitaryServiceStateForBorrower(borrowerEntity, change);
              }}
            >
              <RadioButton
                value="yes"
                labelText="Yes"
                id={`${borrowerEntity}-yes-military-service`}
              />
              <RadioButton value="no" labelText="No" id={`${borrowerEntity}-no-military-service`} />
            </RadioButtonGroup>
          </div>
          {militaryServiceStateForBorrower.ever_serve === true && (
            <>
              <p
                style={{
                  fontStyle: "italic",
                  marginBottom: "10px",
                }}
              >
                Check all that apply, then hit Save.
              </p>
              <CarbonTable>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          margin: "5px 0",
                        }}
                      >
                        <Checkbox
                          id={`${borrowerEntity}-active-duty`}
                          labelText="Currently serving on active duty with projected expiration date of service/tour"
                          checked={militaryServiceStateForBorrower.active_duty}
                          onChange={value => {
                            let change: any = {
                              active_duty: !militaryServiceStateForBorrower.active_duty,
                            };
                            if (!change.active_duty) {
                              // if setting as not on active duty, also
                              // clear the active duty expiration
                              change = {
                                ...change,
                                active_duty_expiration: null,
                              };
                            }
                            updateMilitaryServiceStateForBorrower(borrowerEntity, change);
                          }}
                        />
                        {militaryServiceStateForBorrower.active_duty && (
                          <DatePicker
                            id={`${borrowerEntity}-active-duty-expiration`}
                            datePickerType="single"
                            dateFormat="Y-m-d"
                            value={militaryServiceStateForBorrower.active_duty_expiration}
                            onChange={event => {
                              updateMilitaryServiceStateForBorrower(borrowerEntity, {
                                active_duty_expiration: moment(event[0]).format("YYYY-MM-DD"),
                              });
                            }}
                          >
                            <DatePickerInput
                              id={`${borrowerEntity}-active-duty-expiration-input-id`}
                              labelText=""
                              placeholder="yyyy-mm-dd"
                              type="text"
                              size="sm"
                            />
                          </DatePicker>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Checkbox
                        id={`${borrowerEntity}-retired-discharged-separated`}
                        labelText="Currently retired, discharged, or separated from service"
                        checked={militaryServiceStateForBorrower.retired_discharged_separated}
                        onChange={value =>
                          updateMilitaryServiceStateForBorrower(borrowerEntity, {
                            retired_discharged_separated:
                              !militaryServiceStateForBorrower.retired_discharged_separated,
                          })
                        }
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Checkbox
                        id={`${borrowerEntity}-non-activated-reserve`}
                        labelText="Only period of service was a non-activated member of the Reserve on National Guard"
                        checked={militaryServiceStateForBorrower.non_activated_reserve}
                        onChange={() => {
                          updateMilitaryServiceStateForBorrower(borrowerEntity, {
                            non_activated_reserve:
                              !militaryServiceStateForBorrower.non_activated_reserve,
                          });
                        }}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Checkbox
                        id={`${borrowerEntity}-surviving-spouse`}
                        labelText="Surviving spouse"
                        checked={militaryServiceStateForBorrower.surviving_spouse}
                        onChange={() => {
                          updateMilitaryServiceStateForBorrower(borrowerEntity, {
                            surviving_spouse: !militaryServiceStateForBorrower.surviving_spouse,
                          });
                        }}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </CarbonTable>
            </>
          )}
        </div>
      </>
    );
  };

  const getEffectiveStartAndEndDates = (employment: EmploymentShape) => {
    const { start_date } = employment;
    const end_date = employment.end_date || "current";
    return { start_date, end_date };
  };

  const updateIncomeAggregation = (employmentIndex, aggregationIndex, field, value) => {
    const updatedEmployments = [...employments];
    updatedEmployments[employmentIndex].income_aggregations[aggregationIndex][field] = value;
    setEmployments(updatedEmployments);
  };

  const renderEmployments = (borrower: LoanApplicationBorrower) => {
    const borrowerEntity = borrower.borrower_entity;
    const borrowerName = `${borrower.first_name} ${borrower.last_name}`;
    const employmentHeaderData: TableHeaderDataShape[] = [
      {
        header: "Employer",
        key: "name",
      },
      {
        header: "Employer Contact",
        key: "employer_contact",
        style: { minWidth: "180px" },
      },
      {
        header: "Dates",
        key: "dates",
      },
      {
        header: "Employment",
        key: "employment",
      },
      {
        header: "Main Income",
        key: "is_main_income",
      },
      {
        header: "Monthly Income",
        key: "monthly_income",
      },
      {
        header: "Include In Ratios",
        key: "include_in_ratios",
      },
    ];

    const renderEmploymentRow = (employment: EmploymentShape & { employmentIndex: number }) => {
      const { start_date, end_date } = getEffectiveStartAndEndDates(employment);

      const {
        id,
        name,
        address_line1,
        address_line2,
        city,
        contact,
        employment_type,
        employmentIndex,
        income_aggregations,
        income_type,
        military_income,
        postal_code,
        state,
        tax_income_subsections,
        title,
      } = employment;

      const isMilitary = !!military_income;
      const monthlyIncome = formatCurrency(employment.monthly_income, 2, 2);
      const includeInRatio = !!income_aggregations.find(ia => ia.include_in_ratios);

      return {
        id: `${id}`,
        name: isMilitary ? `US military income (${name})` : name,
        employer_contact: addressToPostalFormatStr(
          address_line1,
          address_line2,
          city,
          state,
          postal_code,
          contact,
        ),
        dates: (
          <span style={{ whiteSpace: "nowrap" }}>
            {start_date} to {end_date}
          </span>
        ),
        is_main_income: (
          <div
            role="button"
            style={{
              display: "flex",
              alignItems: "center",
              whiteSpace: "nowrap",
            }}
            onClick={() => {
              updateMainIncome(employment.id, employment.income_type, employment.borrower_entity);
            }}
            // Accessibility
            onKeyPress={() => {
              updateMainIncome(employment.id, employment.income_type, employment.borrower_entity);
            }}
            tabIndex={0}
          >
            {borrowers[employment.borrower_entity].primary_income_id === employment.id ? (
              <Checkmark32 />
            ) : (
              <Button kind="secondary" size="small" style={{ padding: "5px" }}>
                Select
              </Button>
            )}
          </div>
        ),
        employment: (
          <span>
            {title}/{employment_type === EmploymentType.FullTime ? "FT" : "PT"}/
            {incomeTypeToStr(income_type as IncomeType)}
          </span>
        ),
        monthly_income: isMilitary ? `${monthlyIncome} (total pay & entitlements)` : monthlyIncome,
        include_in_ratios: isMilitary ? (
          <div>
            n/a
            <Tooltip direction="top" tabIndex={0}>
              <p>
                Employment is always included when linked to a military income document specifying
                pay and entitlements
              </p>
            </Tooltip>
          </div>
        ) : (
          <Checkbox
            id={`${id}-include-employment-in-ratio`}
            checked={includeInRatio}
            onChange={() => toggleIncludeInRatios(!includeInRatio, employmentIndex)}
            labelText=""
          />
        ),
        expanded_data: (
          <div className={incomeStyles.expandedDataSection}>
            {income_type === "selfEmployed" && (
              <>
                {tax_income_subsections.length > 0 ? (
                  tax_income_subsections.map(subsection => {
                    return (
                      <div key={subsection.aggregation?.id}>
                        <span style={{ fontWeight: "bold", fontSize: "20px" }}>
                          {TaxReturnIncomeTypeToDisplayText[subsection.aggregation.key]}
                        </span>
                        <TaxSummarySubsection
                          key={subsection.aggregation.key}
                          app={app}
                          subsection={subsection}
                          qualificationYear={income_summary.qualification_year}
                          modifiedAggregations={modifiedAggregations}
                          setModifiedAggregations={setModifiedAggregations}
                        />
                      </div>
                    );
                  })
                ) : (
                  <p>There are no associated tax income sections for this employment.</p>
                )}
              </>
            )}

            {income_type === "salaried" && (
              <EmploymentSection
                update={(index, field, value) => {
                  updateIncomeAggregation(employmentIndex, index, field, value);
                }}
                app={app}
                employment={employment}
              />
            )}
          </div>
        ),
      };
    };

    // Little convenience list
    const employmentData: (EmploymentShape & { employmentIndex: number })[] = employments
      .map((employment, employmentIndex) => ({
        ...employment,
        employmentIndex,
      }))
      .filter(employment => {
        return new BorrowerEntityUtil(employment.borrower_entity).has(borrowerEntity);
      });

    const totalSalariedIncome = calculateMonthlyIncomeForEmployments(employmentData);

    const otherIncomesForCurrentBorrower = otherIncomesByBorrower[borrowerEntity];
    const otherIncomeTotal = calculateMonthlyIncomeForOtherIncomes(otherIncomesForCurrentBorrower);
    let subjectPropertyIncome = Big(0);

    if (Number(borrowerEntity) === 1) {
      subjectPropertyIncome = Big(app.subject_property_rental_income?.net_rental_income);
    }

    // - sub-totals are each displayed independently on this page, rounded to 2 decimal places
    // - so rounding them before summing makes the total = the sum of the displayed (rounded) sub-totals
    const totalIncome = totalSalariedIncome
      .round(2)
      .add(otherIncomeTotal.round(2))
      .add(subjectPropertyIncome.round(2));

    return (
      <div>
        {employments && (
          <>
            <Modal
              danger
              open={deleteModalIsOpen}
              modalHeading="Are you sure you'd like to delete this record?"
              primaryButtonText="Delete"
              secondaryButtonText="Cancel"
              onBlur={() => setDeleteModalIsOpen(false)}
              onRequestClose={() => setDeleteModalIsOpen(false)}
              onRequestSubmit={() => deleteEmployment(employmentFormState.id)}
            />

            <Modal
              danger
              open={batchDeleteModalIsOpen}
              modalHeading="Confirm batch delete"
              primaryButtonText={`Delete ${selectedRows.length} employment${
                selectedRows.length > 1 ? "s" : ""
              }`}
              secondaryButtonText="Cancel"
              onBlur={() => setBatchDeleteModalIsOpen(false)}
              onRequestClose={() => setBatchDeleteModalIsOpen(false)}
              onRequestSubmit={() => {
                updateLoadingState(LoadingState.ACTIVE);

                const promises = selectedRows
                  .map(row => row.id)
                  .map(rowID => {
                    return APIClient.deleteRequest(`/uw/${app.id}/employments/${rowID}`).then(
                      () => {
                        updateEmploymentFormState(initialValues);
                      },
                    );
                  });

                Promise.all(promises).then(() => {
                  updateLoadingState(LoadingState.FINISHED);

                  // Just refresh after a batch delete to reload data
                  window.location.reload();
                });
              }}
            >
              <p style={{ fontSize: "18px", marginBottom: "10px" }}>
                The following employments will be deleted:{" "}
              </p>
              <ul>
                {selectedRows &&
                  selectedRows.map(employment => {
                    const { name, start_date, end_date, borrower_entity } = employment;
                    return (
                      <li key={name} style={{ marginLeft: "1rem" }}>
                        - <span style={{ fontWeight: "bold" }}>Borrower {borrower_entity}: </span>
                        {name} ({start_date} to {end_date || "current"})
                      </li>
                    );
                  })}
              </ul>
            </Modal>

            <Modal
              passiveModal
              open={employmentModalIsOpen}
              modalHeading={
                editingMode === EditingState.CREATE ? "Add Employment" : "Update Employment"
              }
              className={styles.patchModalBottomPaddingIssue}
              selectorPrimaryFocus="#employer-name"
              onRequestClose={() => {
                updateEmploymentFormState(initialValues);
                updateEmploymentModalIsOpen(false);
              }}
              onBlur={() => {
                updateEmploymentFormState(initialValues);
                updateEmploymentModalIsOpen(false);
              }}
              shouldSubmitOnEnter={false}
            >
              {/* Loading spinner for table */}
              <Loading active={loadingState === LoadingState.ACTIVE} />

              <p>Required fields are marked with an asterisk *</p>
              <EmploymentModalForm
                onSubmit={(values: any) => {
                  if (editingMode === EditingState.CREATE) {
                    createEmployment(values);
                  }
                  if (editingMode === EditingState.UPDATE) {
                    updateEmployment(values);
                  }
                }}
                formState={employmentFormState} // Pass the form state for when we edit fields
                borrowers={app.borrowers}
                editingMode={editingMode}
                onRequestClose={() => {
                  updateEmploymentFormState(initialValues);
                  updateEmploymentModalIsOpen(false);
                }}
              />
            </Modal>
            <h3 style={{ marginBottom: 10 }}>
              {borrowerName}: (Total Income {formatCurrency(totalIncome.toFixed(2), 2, 2)})
            </h3>
            <BaseTable
              key={`borrower-employments-${borrowerEntity}`}
              useExpandedRows
              title={`Employments (Total Income Salaried: ${formatCurrency(
                totalSalariedIncome.toFixed(2),
                2,
                2,
              )})`}
              emptyRowText="No Employments"
              disableBatchEdit
              allowHeaderTextWrapping
              onBatchDelete={selectedRawRowsData => {
                setBatchDeleteModalIsOpen(true);
                setSelectedRows(selectedRawRowsData);
              }}
              openModal={() => updateEmploymentModalIsOpen(true)}
              openDeleteModal={() => setDeleteModalIsOpen(true)}
              updateFormState={data => updateEmploymentFormState(data)}
              updateEditingMode={updateEditingMode}
              headerData={employmentHeaderData}
              rawRowData={employmentData}
              rowData={employmentData.map(renderEmploymentRow)}
              expandOverride={rowsExpanded}
              collapseOverride={employmentData.length === 0}
            />
          </>
        )}
      </div>
    );
  };

  // Filtering through all of the incomes is a bit intensive,
  // so we can memoize the value for all existing borrowers with a simple map
  const otherIncomesByBorrower = useMemo(
    () =>
      Object.keys(borrowers).reduce(
        (acc, borrowerEntity) => ({
          ...acc,
          [borrowerEntity]: getOtherIncomesForBorrower(
            app,
            Number(borrowerEntity),
            modifiedAggregations,
            setModifiedAggregations,
          ),
        }),
        {},
      ),
    [app, modifiedAggregations],
  );

  return (
    <HurdleView className={incomeStyles.incomeHurdle} app={app} hurdle={hurdle} onHide={onHide}>
      {Object.keys(borrowers).map(borrowerEntity => {
        const otherIncomesForCurrentBorrower = otherIncomesByBorrower[borrowerEntity];
        const rentals = rentalProperties(app.owned_real_estate);
        return (
          <>
            <div className={incomeStyles.pageControls}>
              <Toggle
                id="toggle-row-expansion"
                toggled={!!rowsExpanded}
                onToggle={() => setRowsExpanded(rowsExpanded ? null : true)}
                labelText="Force expand table rows"
                labelB="On (Individual row expansion disabled)"
              />
            </div>
            {renderEmployments(borrowers[borrowerEntity])}
            <div style={{ marginBottom: "10px" }} />
            <OtherIncomeTable
              app={app}
              loadApp={loadApp}
              borrowerEntity={Number(borrowerEntity)}
              allDocs={all_documents as any}
              additionalIncome={otherIncomesForCurrentBorrower}
              expandOverride={rowsExpanded}
            />
            <div style={{ marginBottom: "10px" }} />
            {Number(borrowerEntity) === 1 &&
              app?.subject_property_rental_income?.gross_rental_income > 0 && (
                <>
                  <SubjectPropertyRentalIncomeTable app={app} expandOverride={rowsExpanded} />
                  <div style={{ marginBottom: "10px" }} />
                </>
              )}
            {rentals.length > 0 && (
              <RentalIncome
                propertyLabelAndAddressOptions={propertyLabelAndAddressOptions}
                propertyLabelAndAddressOptionSelected={(ownedRealEstateID, taxReturnDocument) => {
                  const ownedRealEstateIDsToScheduleEPropertyLabels = rentals.reduce(
                    (acc, rental) => {
                      acc[rental.id] = rental.schedule_e_property_labels.reduce((labels, label) => {
                        labels[label.document_id] = label.property_label;
                        return labels;
                      }, {});
                      return acc;
                    },
                    {},
                  );
                  propertyLabelAndAddressOptionSelected(
                    ownedRealEstateIDsToScheduleEPropertyLabels,
                    ownedRealEstateID,
                    taxReturnDocument,
                  );
                }}
                rentalProperties={rentals}
                setOwnedRealEstateScheduleEPropertyLabel={
                  // this.props.setOwnedRealEstateScheduleEPropertyLabel
                  () => {}
                }
                // setDocId={this.props.setDocId}
                setDocId={() => {}}
                getDocsForOwner={id => documentsForOwner(all_documents, "owned_real_estate", id)}
                taxReturns={taxReturns(all_documents)}
                // handleChange={this.props.handleChange.bind(this, "ownedRealEstate")}
                handleChange={() => {}}
              />
            )}
          </>
        );
      })}

      {renderMixedProperty()}
      {Object.keys(borrowers).map(borrowerEntity => {
        return renderMilitaryService(
          Number(borrowerEntity),
          `${borrowers[borrowerEntity].first_name} ${borrowers[borrowerEntity].last_name}`,
        );
      })}

      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <Toast {...toastHooks} />

      <footer className={styles.footer}>
        <Button kind="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button kind="primary" onClick={onSave} renderIcon={Save16}>
          Save changes
        </Button>
      </footer>
    </HurdleView>
  );
};

export default connect<any, any, any>(null, mapDispatchToProps)(NewIncomeEditor);
